import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Menu, MenuItem } from "semantic-ui-react";
import { get } from "lodash";
import { CoachSettings, LibraryTags, Media, PricePlanTier, WebSettings } from "@/interfaces";
import { usePlayerSettings, URLS, useAccountSettings, useHasBlogAccess, useSocialCoachSession } from "@/utils";

import { PlayerVideoCatalyst } from "./components/PlayerVideoCatalyst";
import "./styles.scss";
import { PlayerVideoScripts } from "./components/PlayerVideoScripts";
import { PlayerPrompts } from "./components/PlayerPrompts";
import { NewOptionsModal } from "./components/NewOptionsModal";
import { useParams } from "react-router-dom";
import { VideoPageModal } from "@/containers/VideoPages/VideoPageModal";
import { VideoPagesList } from "@/containers/VideoPages/VideoPagesList";
import { PlayerBlogHomePage } from "@/containers/Blogs";
import { PageWrapper } from "@/components/PageWrapper";
import { SuperPageAssignmentsList } from "@/containers/SuperPages";
import { fromMedia } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { ShareFlowHost } from "@/components/ShareDesignPost/ShareFlowHost";

interface SettingMenu {
  title: string;
  tab: LibraryTags;
  hideMenu?: boolean;
  content?: React.ReactNode;
}

const LibraryFC: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ showNew?: boolean }>();
  const { tab } = useParams<{ tab: LibraryTags }>();
  const showNew: boolean =
    !!get(location, "state.showNew", false) || new URLSearchParams(location.search).get("new") === "true";

  const { coachId, playerId, session } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(playerId);
  const hasSCPlus = session?.subscription?.tier === PricePlanTier.PLAYER_PLUS;
  const { hasAccountSetting } = useAccountSettings();
  const hasScriptLibrary = useMemo(() => {
    return hasSCPlus || hasAccountSetting(WebSettings.ACCESS_SCRIPT_LIBRARY);
  }, [hasAccountSetting]);
  const hasScreenCapture = useMemo(() => hasSetting(CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE, true), [hasSetting]);
  const hasMVP = useMemo(() => {
    return hasSetting(CoachSettings.PLAYER_MVP_ACCESS);
  }, [hasSetting]);
  const hasBlogs = useHasBlogAccess(playerId);

  const [showNewModal, setShowNewModal] = useState(showNew);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [chosenMedia, setChosenMedia] = useState<Media>();

  const selectTab = (tab: LibraryTags) => {
    history.replace(URLS.player.libraryTab.replace(":tab?", tab));
  };

  const onNewMedia = (media: Media, type: "post" | "videoPage" | "videoProject") => {
    switch (type) {
      case "post":
        setChosenMedia(media);
        setShowNewPostModal(true);
        break;
      case "videoPage":
        setChosenMedia(media);
        setShowNewPageModal(true);
        break;
      case "videoProject":
        history.push(URLS.player.newVideoProject, { preloadMedia: media });
    }
  };

  const handleDoneWithMedia = () => {
    setChosenMedia(undefined);
    setShowNewPostModal(false);
    setShowNewPageModal(false);
  };

  const defaultNewOption = useMemo(() => {
    switch (tab) {
      case "prompts":
        return "post";
      case "video-projects":
        return "videoProject";
      case "scripts":
        return "script";
      case "video-pages":
      case "video-page-assignments":
      // Blogs are generated from a Video Page, so that is the useful default here too.
      case "blogs":
        return "videoPage";
    }
  }, [tab]);

  const menuList = useMemo(() => {
    let menu: SettingMenu[] = [
      { title: "Prompts", tab: "prompts", content: <PlayerPrompts coachId={coachId!!} playerId={playerId!!} /> },
    ];
    if (hasSCPlus) {
      menu.push({ title: "Video Catalyst", tab: "video-projects", content: <PlayerVideoCatalyst /> });
    }
    if (hasScriptLibrary) {
      menu.push({ title: "Scripts", tab: "scripts", content: <PlayerVideoScripts /> });
    }
    if (hasMVP) {
      menu.push({ title: "Video Pages", tab: "video-pages", content: <VideoPagesList /> });
      menu.push({
        title: "Video Page Assignments",
        tab: "video-page-assignments",
        hideMenu: true,
        content: <SuperPageAssignmentsList />,
      });
    }
    if (hasBlogs) {
      menu.push({
        title: "Blogs",
        tab: "blogs",
        content: <PlayerBlogHomePage />,
      });
    }

    return menu;
  }, [hasScriptLibrary, hasMVP, hasBlogs, hasSCPlus, coachId, playerId]);

  const selectedContent = useMemo(() => {
    const content = menuList.find(m => m.tab === tab)?.content;
    if (content) {
      return content;
    } else {
      selectTab("prompts");
      return <></>;
    }
  }, [tab, menuList]);

  const showMenu = useMemo(() => {
    return menuList.find(m => m.tab === tab)?.hideMenu !== true;
  }, [tab, menuList]);

  const handleNewPageModalClose = () => {
    handleDoneWithMedia();
    setShowNewPageModal(false);
  };

  const videoPageModalInitialObject = useMemo(() => {
    if (chosenMedia) return { media: chosenMedia };
    return undefined;
  }, [chosenMedia]);

  return (
    <PageWrapper
      title={showMenu ? "Library" : undefined}
      actions={showMenu ? [{ text: "New...", onClick: () => setShowNewModal(true) }] : undefined}
      hideSeparator
      className={tab === "blogs" ? undefined : "max-w-325"}
    >
      {showMenu && (
        <Menu pointing secondary className="internalTapMenu">
          {menuList.map(element => {
            if (element.hideMenu) return null;
            return (
              <MenuItem
                key={`menu-item-${element.tab}`}
                name={element.title}
                active={tab === element.tab}
                onClick={() => selectTab(element.tab)}
              />
            );
          })}
        </Menu>
      )}
      <ShareFlowHost playerId={playerId != null ? Number(playerId) : null}>{selectedContent}</ShareFlowHost>

      <NewOptionsModal
        playerId={playerId}
        open={showNewModal}
        defaultOption={defaultNewOption}
        onClose={() => {
          setShowNewModal(false);
          const searchParams = new URLSearchParams(location.search);
          if (searchParams.get("new")) {
            searchParams.delete("new");
            const newSearch = searchParams.toString();
            history.replace({
              pathname: location.pathname,
              search: newSearch ? `?${newSearch}` : "",
            });
          }
        }}
        hasSCPlus={hasSCPlus}
        hasScriptLibrary={hasScriptLibrary}
        hasScreenCapture={hasScreenCapture !== false}
        hasMVP={!!hasMVP}
        onMediaSelected={onNewMedia}
      />

      {showNewPostModal && (
        <PlayerPostDialog open={showNewPostModal} handleClose={handleDoneWithMedia} defaults={fromMedia(chosenMedia)} />
      )}
      <VideoPageModal
        open={showNewPageModal}
        initialObject={videoPageModalInitialObject}
        onClose={handleNewPageModalClose}
      />
    </PageWrapper>
  );
};

export const PlayerLibrary = LibraryFC;
